import React, { useState, useEffect, Fragment, useRef, useCallback } from 'react';
import { FaUsers, FaLaptop, FaClock, FaFileExcel, FaEye, FaEraser } from 'react-icons/fa';
import { SearchIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/outline';
import { Dialog, Transition } from '@headlessui/react';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import userHistoryService from '../../../../services/userHistory';
import DeleteConfirmModal from '../../../../components/common/DeleteConfirmModal';
import { toastService } from '../../../../services/toast';

// ===== Helper Functions =====

// دالة تنسيق التاريخ والوقت
const formatDateTime = (dateTimeStr) => {
  if (!dateTimeStr) return '--';
  const date = new Date(dateTimeStr);
  const hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12;
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  
  return `${day}/${month}/${year} ${hours12}:${minutes} ${period}`;
};

// دالة لحساب مدة الاستخدام
const calculateDuration = (loginTime, logoutTime) => {
  if (!loginTime || !logoutTime) return '--:--';
  
  try {
    const loginDate = new Date(loginTime);
    const logoutDate = new Date(logoutTime);
    
    if (isNaN(loginDate.getTime()) || isNaN(logoutDate.getTime())) {
      return '--:--';
    }
    
    const duration = logoutDate - loginDate;
    
    if (duration < 0) {
      return '--:--';
    }
    
    const hours = Math.floor(duration / 3600000);
    const minutes = Math.floor((duration % 3600000) / 60000);
    
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  } catch (error) {
    return '--:--';
  }
};

// دالة لتنسيق الوقت
const formatTime = (dateTimeStr) => {
  if (!dateTimeStr) return '--';
  try {
    const date = new Date(dateTimeStr);
    if (isNaN(date.getTime())) return '--';
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  } catch (error) {
    return '--';
  }
};

// ===== Skeleton Component =====
const UsersSkeleton = () => {
  return (
    <div className="space-y-6 p-6 animate-pulse">
      {/* Stats Cards Skeleton - بالضبط نفس مقاسات الكروت الأصلية */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[1, 2, 3].map((item) => (
          <div key={item} className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 h-[120px]">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-gray-200 dark:bg-gray-700 rounded-full w-14 h-14"></div>
              <div className="flex-1">
                <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-32 mb-2"></div>
                <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-20"></div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Filter Section Skeleton - بالضبط نفس مقاسات قسم البحث الأصلي */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-28 mb-2"></div>
            <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
          </div>
          <div>
            <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-28 mb-2"></div>
            <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
          </div>
          <div>
            <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-28 mb-2"></div>
            <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
          </div>
        </div>
      </div>

      {/* Table Skeleton - بالضبط نفس مقاسات الجدول الأصلي */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-48"></div>
          <div className="flex space-x-2 rtl:space-x-reverse">
            <div className="h-8 w-8 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div className="h-8 w-8 bg-gray-200 dark:bg-gray-700 rounded"></div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                {/* بالضبط نفس عدد الأعمدة في الجدول الأصلي */}
                {[1, 2, 3, 4, 5, 6, 7].map((item) => (
                  <th key={item} scope="col" className="px-6 py-3">
                    <div className="h-5 bg-gray-200 dark:bg-gray-600 rounded w-24"></div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {/* بالضبط نفس عدد الصفوف في الجدول الأصلي */}
              {[1, 2, 3, 4, 5].map((row) => (
                <tr key={row}>
                  {[1, 2, 3, 4, 5, 6, 7].map((cell) => (
                    <td key={`${row}-${cell}`} className="px-6 py-4 whitespace-nowrap">
                      <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-full max-w-[120px]"></div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex justify-between items-center">
            <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-32"></div>
            <div className="flex space-x-2 rtl:space-x-reverse">
              {[1, 2, 3, 4, 5].map((item) => (
                <div key={item} className="h-8 w-8 bg-gray-200 dark:bg-gray-700 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ===== Main Component =====
const UserInfoPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searching, setSearching] = useState(false);
  const [pagination, setPagination] = useState(null);
  const [stats, setStats] = useState({ active_users: 0, devices_used: 0, total_usage_time: '0:00' });
  const [userSessions, setUserSessions] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showClearConfirmModal, setShowClearConfirmModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const searchTimeoutRef = useRef(null);

  // جلب الإحصائيات
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await userHistoryService.getLoginHistoryStats();
        if (res?.status === 'success' && res?.data) {
          setStats(res.data);
        }
      } catch (err) {
        console.error('Error fetching login history stats:', err);
      }
    };
    fetchStats();
  }, []);

  // جلب البيانات الأولية
  useEffect(() => {
    const fetchInitial = async () => {
      try {
        const response = await userHistoryService.searchUserLoginHistory({ per_page: 20 });
        if (response?.status === 'success') {
          setUsers(response.data || []);
          setPagination(response.pagination || null);
        } else {
          setError('حدث خطأ أثناء جلب البيانات');
        }
      } catch (error) {
        console.error('Error fetching user login history:', error);
        setError('حدث خطأ أثناء الاتصال بالخادم');
      } finally {
        setLoading(false);
      }
    };
    
    fetchInitial();
  }, []);

  // تنظيف التايمر
  useEffect(() => {
    return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); };
  }, []);

  const fetchUsers = useCallback(async (page = 1, search = '') => {
    try {
      const params = { per_page: 20, page };
      if (search.trim()) params.search = search.trim();
      const response = await userHistoryService.searchUserLoginHistory(params);
      if (response?.status === 'success') {
        setUsers(response.data || []);
        setPagination(response.pagination || null);
      }
    } catch {
      setUsers([]);
      setPagination(null);
    }
  }, []);

  const handleSearchChange = useCallback((value) => {
    setSearchQuery(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (!value.trim()) {
      setSearching(false);
      setCurrentPage(1);
      fetchUsers(1, '');
      return;
    }
    setSearching(true);
    searchTimeoutRef.current = setTimeout(async () => {
      setCurrentPage(1);
      await fetchUsers(1, value);
      setSearching(false);
    }, 300);
  }, [fetchUsers]);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
    fetchUsers(page, searchQuery);
  }, [searchQuery, fetchUsers]);

  // جلب جميع سجلات دخول المستخدم المحدد
  useEffect(() => {
    const fetchUserSessions = async () => {
      if (isModalOpen && selectedUser) {
        try {
          const userId = selectedUser.user_id || selectedUser.id || selectedUser.name;
          const response = await userHistoryService.fetchUserLoginHistoryById(userId);
          
          if (response.status === 'success' && response.data) {
            setUserSessions(response.data);
          } else {
            setUserSessions([selectedUser]);
          }
        } catch (error) {
          console.error('Error fetching user sessions:', error);
          setUserSessions([selectedUser]);
        }
      } else {
        setUserSessions([]);
      }
    };
    
    fetchUserSessions();
  }, [isModalOpen, selectedUser]);
  
  // دالة لعرض أجهزة المستخدم
  const handleViewDevices = (user) => {
    setSelectedUser(user);
    setIsModalOpen(true);
  };
  
  // جلب جميع البيانات للتصدير
  const fetchAllForExport = async () => {
    try {
      const response = await userHistoryService.fetchUserLoginHistory();
      if (response?.status === 'success' && Array.isArray(response.data)) {
        return response.data;
      }
    } catch (error) {
      console.error('Error fetching all data for export:', error);
    }
    return [];
  };

  // تصدير إلى Excel
  const exportToExcel = async () => {
    const allData = await fetchAllForExport();
    if (allData.length === 0) {
      toastService.error('لا توجد بيانات للتصدير');
      return;
    }
    const data = allData.map(user => ({
      'اسم المستخدم': user.name,
      'نظام التشغيل': user.os || 'غير معروف',
      'المتصفح': user.browser || 'غير معروف',
      'وقت الدخول': formatTime(user.login_time) || '--',
      'وقت الخروج': formatTime(user.logout_time) || '--',
      'مدة الاستخدام': calculateDuration(user.login_time, user.logout_time),
      'عنوان IP': user.ip_address || '--'
    }));

    const ws = XLSX.utils.json_to_sheet(data, { origin: 'A1' });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'معلومات المستخدمين');
    XLSX.writeFile(wb, 'users_info.xlsx');
  };

  // تصدير إلى PDF
  const exportToPDF = async () => {
    const allData = await fetchAllForExport();
    if (allData.length === 0) {
      toastService.error('لا توجد بيانات للتصدير');
      return;
    }
    try {
      const doc = new jsPDF();
      
      // إضافة عنوان للملف
      doc.setFont('Helvetica', 'bold');
      doc.setFontSize(18);
      doc.text('تقرير معلومات المستخدمين', 105, 15, { align: 'center' });
      
      // إضافة تاريخ التقرير
      doc.setFont('Helvetica', 'normal');
      doc.setFontSize(12);
      doc.text(`تاريخ التقرير: ${new Date().toLocaleDateString('ar-SA')}`, 105, 25, { align: 'center' });
      
      // إعداد بيانات الجدول
      const tableData = allData.map(user => [
        user.name || '',
        user.os || 'Windows',
        user.browser || 'Firefox',
        formatDateTime(user.login_time) || '',
        formatDateTime(user.logout_time) || '',
        calculateDuration(user.login_time, user.logout_time) || ''
      ]);
      
      // إضافة الجدول
      autoTable(doc, {
        head: [['اسم المستخدم', 'نظام التشغيل', 'المتصفح', 'وقت الدخول', 'وقت الخروج', 'مدة الاستخدام']],
        body: tableData,
        startY: 35,
        styles: { halign: 'right', font: 'Helvetica' },
        headStyles: { fillColor: [41, 128, 185], textColor: 255 },
        alternateRowStyles: { fillColor: [240, 240, 240] },
        margin: { right: 15, left: 15 }
      });
      
      // حفظ الملف
      doc.save('user_info_report.pdf');
      
    } catch (error) {
      console.error('خطأ في تصدير PDF:', error);
    }
  };
  
  // تفريغ معلومات المستخدمين
  const handleClearUserInfo = () => {
    setShowClearConfirmModal(true);
  };
  
  // تنفيذ عملية تفريغ معلومات المستخدمين
  const confirmClearUserInfo = async () => {
    try {
      // استدعاء API لتفريغ جدول user_login_history
      await userHistoryService.clearAllLoginHistory();
      
      // تحديث واجهة المستخدم
      setUsers([]);
      setPagination(null);
      setShowClearConfirmModal(false);
      
      // إظهار رسالة نجاح
      toastService.success('تم تفريغ سجلات تسجيل دخول المستخدمين بنجاح');
    } catch (error) {
      console.error('خطأ في تفريغ سجلات تسجيل دخول المستخدمين:', error);
      toastService.error('حدث خطأ أثناء تفريغ سجلات تسجيل دخول المستخدمين');
    }
  };

  // للتطوير: عرض الشكلتون بشكل دائم لضبط المقاسات
  // احذف التعليق عن السطر التالي لعرض الشكلتون بشكل دائم
  // return <UsersSkeleton />;
  
  // إذا كان هناك تحميل، عرض الشكلتون
  if (loading) {
    return <UsersSkeleton />;
  }
  
  // إذا كان هناك خطأ، عرض رسالة الخطأ
  if (error) {
    return (
      <div className="p-6 text-center">
        <div className="text-red-500 text-xl mb-4">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="font-bold">حدث خطأ</h3>
        </div>
        <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
        <button 
          onClick={() => window.location.reload()} 
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
        >
          إعادة المحاولة
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Stats Cards Section */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Active Users Card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-100 dark:bg-blue-900 rounded-full">
              <FaUsers className="text-blue-600 dark:text-blue-300 text-xl" />
            </div>
            <div>
              <h3 className="text-gray-500 dark:text-gray-400 text-sm">المستخدمين النشطين</h3>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.active_users}</p>
            </div>
          </div>
        </div>

        {/* Active Devices Card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-green-100 dark:bg-green-900 rounded-full">
              <FaLaptop className="text-green-600 dark:text-green-300 text-xl" />
            </div>
            <div>
              <h3 className="text-gray-500 dark:text-gray-400 text-sm">الأجهزة المستخدمة</h3>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.devices_used}</p>
            </div>
          </div>
        </div>

        {/* Total Daily Usage Card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-purple-100 dark:bg-purple-900 rounded-full">
              <FaClock className="text-purple-600 dark:text-purple-300 text-xl" />
            </div>
            <div>
              <h3 className="text-gray-500 dark:text-gray-400 text-sm">إجمالي وقت الاستخدام اليومي</h3>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.total_usage_time}</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Filters Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Search Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              البحث عن مستخدم
            </label>
            <div className="relative rounded-lg">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="input-base pr-10"
                placeholder="البحث..."
              />
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                {searching ? (
                  <svg className="animate-spin h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                  </svg>
                ) : (
                  <SearchIcon className="h-5 w-5 text-blue-500 dark:text-blue-400" />
                )}
              </div>
            </div>
          </div>

          {/* Username Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              اسم المستخدم
            </label>
            <select className="input-base">
              <option value="">الكل</option>
              {users.map((user, index) => (
                <option key={index} value={user.name}>{user.name}</option>
              ))}
            </select>
          </div>

          {/* Operating System Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              نظام التشغيل
            </label>
            <select className="input-base">
              <option value="">الكل</option>
              <option value="windows">Windows</option>
              <option value="mac">MacOS</option>
              <option value="linux">Linux</option>
            </select>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <h2 className="text-lg font-medium text-gray-900 dark:text-white">معلومات المستخدمين</h2>
          <div className="flex space-x-2 rtl:space-x-reverse">
            <button
              onClick={handleClearUserInfo}
              className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-4 py-2 rounded-md hover:from-purple-700 hover:to-indigo-700 transition-all shadow-md flex items-center gap-2 text-sm font-medium"
              title="تفريغ معلومات المستخدمين"
            >
              <FaEraser className="h-4 w-4" />
              تفريغ معلومات المستخدمين
            </button>
          </div>
        </div>

        {/* Table Content */}
        <div className="relative overflow-x-auto">
          <div className="min-w-full">
            <table className="w-full">
              <thead className="bg-gray-800 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-right text-xs font-medium text-white dark:text-white uppercase tracking-wider">
                    اسم المستخدم
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-white dark:text-white uppercase tracking-wider">
                    نظام التشغيل
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-white dark:text-white uppercase tracking-wider">
                    المتصفح
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-white dark:text-white uppercase tracking-wider">
                    وقت الدخول
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-white dark:text-white uppercase tracking-wider">
                    وقت الخروج
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-white dark:text-white uppercase tracking-wider">
                    مدة الاستخدام
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-white dark:text-white uppercase tracking-wider">
                    عنوان IP
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-white dark:text-white uppercase tracking-wider">
                    الإجراءات
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {users.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                      <div className="flex flex-col items-center justify-center">
                        <svg className="w-12 h-12 mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                        </svg>
                        <p className="text-lg font-medium">لا توجد بيانات حالية للعرض</p>
                        <p className="mt-2">جرب تغيير معايير البحث أو التصفية</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  users.map((user, index) => (
                    <tr key={user.id || index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-gray-900 dark:text-white">{user.name}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-900 dark:text-white">{user.os || 'غير معروف'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-900 dark:text-white">{user.browser || 'غير معروف'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-900 dark:text-white">{formatTime(user.login_time) || '--'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-900 dark:text-white">{formatTime(user.logout_time) || '--'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-900 dark:text-white font-mono">
                        {calculateDuration(user.login_time, user.logout_time)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-900 dark:text-white">
                        {user.ip_address || '--'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleViewDevices(user)}
                          className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300"
                        >
                          <FaEye className="text-lg" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        {/* Pagination controls */}
        <div className="flex items-center justify-center bg-gray-50 dark:bg-gray-700 px-4 py-3 border-t border-gray-200 dark:border-gray-600 rounded-b-lg">
          <div className="flex items-center">
            <button 
              className="p-2 text-blue-600 hover:text-blue-800 transition-colors duration-200"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage <= 1}
            >
              <ChevronRightIcon className="h-5 w-5" />
            </button>
            <span className="mx-3 text-sm font-medium text-gray-700 dark:text-gray-300">
              {currentPage} / {pagination?.last_page || 0}
            </span>
            <button 
              className="p-2 text-blue-600 hover:text-blue-800 transition-colors duration-200"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage >= (pagination?.last_page || 0)}
            >
              <ChevronLeftIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Devices Modal */}
      {isModalOpen && selectedUser && (
        <Transition appear show={isModalOpen} as={Fragment}>
          <Dialog
            as="div"
            className="relative z-50"
            onClose={() => setIsModalOpen(false)}
          >
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black/25 backdrop-blur-sm" />
            </Transition.Child>
    
            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-6xl transform overflow-visible rounded-lg bg-white dark:bg-gray-800 p-6 shadow-xl transition-all">
                    <div className="flex justify-between items-center mb-4">
                      <Dialog.Title className="text-lg font-medium text-gray-900 dark:text-white">
                        {selectedUser.name}
                      </Dialog.Title>
                      <div className="flex items-center gap-4">
                        <button
                          onClick={() => exportToExcel(true)}
                          className="text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300"
                          title="تصدير إلى Excel"
                        >
                          <FaFileExcel className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
    
                    {/* معلومات المستخدم */}
                    <div className="mt-6">
                      <div className="overflow-x-auto">
                        <table className="w-full divide-y divide-gray-200 dark:divide-gray-700">
                          <colgroup>
                            <col className="w-[15%]" /> {/* اسم المستخدم */}
                            <col className="w-[12%]" /> {/* المنطقة */}
                            <col className="w-[12%]" /> {/* نظام التشغيل */}
                            <col className="w-[12%]" /> {/* المتصفح */}
                            <col className="w-[16%]" /> {/* وقت الدخول */}
                            <col className="w-[16%]" /> {/* وقت الخروج */}
                            <col className="w-[15%]" /> {/* مدة الاستخدام */}
                          </colgroup>
                          <thead className="bg-gray-800 dark:bg-gray-700">
                            <tr>
                              <th scope="col" className="px-3 py-3 text-right text-xs font-medium text-white dark:text-white uppercase tracking-wider">
                                اسم المستخدم
                              </th>
                              <th scope="col" className="px-3 py-3 text-right text-xs font-medium text-white dark:text-white uppercase tracking-wider">
                                المنطقة
                              </th>
                              <th scope="col" className="px-3 py-3 text-right text-xs font-medium text-white dark:text-white uppercase tracking-wider">
                                نظام التشغيل
                              </th>
                              <th scope="col" className="px-3 py-3 text-right text-xs font-medium text-white dark:text-white uppercase tracking-wider">
                                المتصفح
                              </th>
                              <th scope="col" className="px-3 py-3 text-right text-xs font-medium text-white dark:text-white uppercase tracking-wider">
                                وقت الدخول
                              </th>
                              <th scope="col" className="px-3 py-3 text-right text-xs font-medium text-white dark:text-white uppercase tracking-wider">
                                وقت الخروج
                              </th>
                              <th scope="col" className="px-3 py-3 text-right text-xs font-medium text-white dark:text-white uppercase tracking-wider">
                                مدة الاستخدام
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {userSessions.map((session, index) => (
                              <tr key={index} className={index % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-700'}>
                                <td className="px-3 py-2 text-sm text-gray-900 dark:text-white whitespace-nowrap">
                                  {session.name || ''}
                                </td>
                                <td className="px-3 py-2 text-sm text-gray-900 dark:text-white">
                                  {session.region || 'Asia/Riyadh'}
                                </td>
                                <td className="px-3 py-2 text-sm text-gray-900 dark:text-white">
                                  {session.os || 'Windows'}
                                </td>
                                <td className="px-3 py-2 text-sm text-gray-900 dark:text-white">
                                  {session.browser || 'Firefox'}
                                </td>
                                <td className="px-3 py-2 text-sm text-gray-900 dark:text-white">
                                  {formatTime(session.login_time) || '3:36 PM'}
                                </td>
                                <td className="px-3 py-2 text-sm text-gray-900 dark:text-white">
                                  {formatTime(session.logout_time) || '3:37 PM'}
                                </td>
                                <td className="px-3 py-2 text-sm text-gray-900 dark:text-white">
                                  {calculateDuration(session.login_time, session.logout_time) || '00:01'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot className="bg-gray-800 dark:bg-gray-700">
                            <tr>
                              <td colSpan="9" className="px-3 py-3">
                                <div className="flex justify-between items-center">
                                  <div className="text-sm text-white dark:text-white">
                                    <span>إجمالي الدخول: {userSessions.length}</span>
                                  </div>
                                  <div className="flex gap-4 text-sm text-white dark:text-white">
                                    <span>Windows: {userSessions.filter(session => session.os && session.os.toLowerCase().includes('windows')).length || 0}</span>
                                    <span>Mac: {userSessions.filter(session => session.os && session.os.toLowerCase().includes('mac')).length || 0}</span>
                                    <span>iOS: {userSessions.filter(session => session.os && session.os.toLowerCase().includes('ios')).length || 0}</span>
                                    <span>Android: {userSessions.filter(session => session.os && session.os.toLowerCase().includes('android')).length || 0}</span>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>
      )}

      {/* مكون تأكيد تفريغ معلومات المستخدمين */}
      <DeleteConfirmModal
        isOpen={showClearConfirmModal}
        onClose={() => setShowClearConfirmModal(false)}
        onConfirm={confirmClearUserInfo}
        itemType="معلومات المستخدمين"
        customMessage="هل أنت متأكد من تفريغ جميع معلومات المستخدمين؟"
        note="لن يمكن استعادة هذه المعلومات بعد التفريغ"
      />
    </div>
  );
};

export default UserInfoPage;
